/* eslint-disable react-hooks/exhaustive-deps */
import { Illustration, Table, Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box, Center } from 'native-base';
import React, { useMemo } from 'react';
import { DimensionValue, FlatList } from 'react-native';

import { SentOrdersListResponseNormalized } from '../../../../store/rateManagement/selector';
import { makeLoadingTable } from '../../../../utils/makeLoadingTable';

import { Head } from './components/Head';
import { SentOrderListItem } from './components/Item';
import { rowHeight, loadingCard } from './constants';

export interface ItemData {
  sendId: string;
  monthCompetence: string;
  type: string;
  billedCustomers: number;
  sendDate: string;
  total: string;
  status: string;
}

interface SentOrderListProps {
  isMobile?: boolean;
  sentOrdersLoading?: boolean;
  sentOrders?: SentOrdersListResponseNormalized[];
  onLoadMore?: () => void;
  hasSearch?: boolean;
}

export const SentOrderList: React.FC<SentOrderListProps> = ({
  isMobile,
  sentOrders,
  sentOrdersLoading,
  onLoadMore,
  hasSearch,
}) => {
  const loadingTable = useMemo(() => makeLoadingTable({ numRows: 4, numCols: 7 }), []);
  const loadingCardItems = useMemo(() => Array.from({ length: 3 }, (_) => loadingCard), []);
  const deviceType = useBreakpointValue({ lg: 'desktop', md: 'tablet' });

  const content = useMemo(() => {
    if (!sentOrders?.length && !sentOrdersLoading) {
      return (
        <Center flex={1}>
          <VStack alignItems="center" space={Spacing['stack.quarck']}>
            <Illustration
              name={hasSearch ? 'NotFoundIllustration' : 'NotEmptyIllustration'}
              testID={'empty-illustration'}
            />
            <Text
              textAlign={'center'}
              paddingTop={Spacing['stack.xxxs']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              variant="bold"
              color="neutral.black">
              {hasSearch
                ? 'Nenhum resultado corresponde a sua pesquisa'
                : 'Você ainda não enviou nenhuma solicitação'}
            </Text>
            <Text
              textAlign={'center'}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.black">
              {hasSearch
                ? 'Por favor, verifique o campo informado e tente novamente'
                : 'Lembre-se de enviar suas solicitações de gerenciamento de taxas.'}
            </Text>
            {!hasSearch && (
              <Box alignItems="center" flexDir="row">
                <Text
                  fontSize={Utils.Responsive.All({
                    phone: Typography['fontsize.xs'],
                    tablet: Typography['fontsize.xs'],
                    desktop: Typography['fontsize.md'],
                  })}
                  color="neutral.black">
                  Basta clicar no botão{' '}
                </Text>
                <Text color="brand.tertiary.principal" variant={'semibold'}>
                  NOVA SOLICITAÇÃO.
                </Text>
              </Box>
            )}
          </VStack>
        </Center>
      );
    }

    return (
      <VStack flex={1}>
        {isMobile ? (
          <FlatList
            data={sentOrders}
            windowSize={10}
            maxToRenderPerBatch={4}
            contentContainerStyle={{ flex: 1 }}
            renderItem={({ item }) => <SentOrderListItem loading={sentOrdersLoading} item={item} />}
            keyExtractor={(item) => item.managementFeeIdNormalized.toString()}
            showsVerticalScrollIndicator={false}
            onEndReached={onLoadMore}
            ListFooterComponent={sentOrdersLoading ? <>{loadingCardItems}</> : null}
          />
        ) : (
          <Table
            testID="table"
            isLoading={!sentOrders?.length && !!sentOrdersLoading}
            rows={4}
            cols={7}
            rowHeight={rowHeight}
            flex={1}>
            {deviceType === 'desktop' && <Head />}
            <FlatList
              updateCellsBatchingPeriod={5}
              data={sentOrders}
              windowSize={10}
              maxToRenderPerBatch={4}
              style={{
                flex: 1,
                marginLeft: `-${Spacing['inline.xxs']}` as DimensionValue,
                marginRight: `-${Spacing['inline.xxs']}` as DimensionValue,
              }}
              renderItem={({ item }) => <SentOrderListItem item={item} />}
              keyExtractor={(item) => item.managementFeeIdNormalized.toString()}
              showsVerticalScrollIndicator={false}
              onEndReached={onLoadMore}
              ListFooterComponent={sentOrdersLoading ? loadingTable : null}
            />
          </Table>
        )}
      </VStack>
    );
  }, [sentOrders, sentOrdersLoading, isMobile, onLoadMore, loadingCard, deviceType, loadingTable]);

  return <Box flex={1}>{content}</Box>;
};
